import { useState, useCallback } from 'react';
import { AlertType, AlertPosition, AlertVariant } from '../shared/components/alert/SharedAlert';
import { 
    trackExceptionWithContext
} from '../services/application-insights/appInsights';
import { 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../services/application-insights/appInsightsConstants';

export interface IAlertConfig {
	id: string;
	type: AlertType;
	message: string;
	title?: string;
	autoDismissTime?: number;
	position?: AlertPosition;
	variant?: AlertVariant;
	className?: string;
	panelOffsets?: { top?: number; left?: number; right?: number; bottom?: number };
	closable?: boolean;
	zIndex?: number;
}

export interface IUseSharedAlertsReturn {
	alerts: IAlertConfig[];
	showAlert: (config: Omit<IAlertConfig, 'id'>) => string;
	showSuccess: (message: string, options?: Partial<Omit<IAlertConfig, 'id' | 'type' | 'message'>>) => string;
	showError: (message: string, options?: Partial<Omit<IAlertConfig, 'id' | 'type' | 'message'>>) => string;
	showWarning: (message: string, options?: Partial<Omit<IAlertConfig, 'id' | 'type' | 'message'>>) => string;
	showInfo: (message: string, options?: Partial<Omit<IAlertConfig, 'id' | 'type' | 'message'>>) => string;
	hideAlert: (id: string) => void;
	clearAllAlerts: () => void;
}

export const useSharedAlerts = (): IUseSharedAlertsReturn => {
	const [alerts, setAlerts] = useState<IAlertConfig[]>([]);

	const generateId = () => `alert-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

	const showAlert = useCallback((config: Omit<IAlertConfig, 'id'>): string => {
		try {
			const id = generateId();
			const newAlert: IAlertConfig = {
				id,
				...config,
			};

			setAlerts((prev) => [...prev, newAlert]);
			return id;
		} catch (error) {
			trackExceptionWithContext(
				error instanceof Error ? error : new Error('Failed to show alert'),
				{
					component: AppInsightsComponents.SHARED_ALERTS_HOOK,
					additionalData: {
						errorType: AppInsightsErrorTypes.ALERT_SYSTEM_ERROR,
						operation: 'showAlert',
						alertType: config.type
					}
				}
			);
			throw error;
		}
	}, []);

	const showSuccess = useCallback((message: string, options?: Partial<Omit<IAlertConfig, 'id' | 'type' | 'message'>>): string => {
		return showAlert({
			type: 'success',
			message,
			...options,
		});
	}, [showAlert]);

	const showError = useCallback((message: string, options?: Partial<Omit<IAlertConfig, 'id' | 'type' | 'message'>>): string => {
		return showAlert({
			type: 'error',
			message,
			autoDismissTime: 0, // Errors should not auto-dismiss by default
			...options,
		});
	}, [showAlert]);

	const showWarning = useCallback((message: string, options?: Partial<Omit<IAlertConfig, 'id' | 'type' | 'message'>>): string => {
		return showAlert({
			type: 'warning',
			message,
			autoDismissTime: 8000, // Warnings should stay a bit longer
			...options,
		});
	}, [showAlert]);

	const showInfo = useCallback((message: string, options?: Partial<Omit<IAlertConfig, 'id' | 'type' | 'message'>>): string => {
		return showAlert({
			type: 'info',
			message,
			...options,
		});
	}, [showAlert]);

	const hideAlert = useCallback((id: string) => {
		try {
			setAlerts((prev) => prev.filter((alert) => alert.id !== id));
		} catch (error) {
			trackExceptionWithContext(
				error instanceof Error ? error : new Error('Failed to hide alert'),
				{
					component: AppInsightsComponents.SHARED_ALERTS_HOOK,
					additionalData: {
						errorType: AppInsightsErrorTypes.ALERT_SYSTEM_ERROR,
						operation: 'hideAlert',
						alertId: id
					}
				}
			);
			throw error;
		}
	}, []);

	const clearAllAlerts = useCallback(() => {
		try {
			setAlerts([]);
		} catch (error) {
			trackExceptionWithContext(
				error instanceof Error ? error : new Error('Failed to clear all alerts'),
				{
					component: AppInsightsComponents.SHARED_ALERTS_HOOK,
					additionalData: {
						errorType: AppInsightsErrorTypes.ALERT_SYSTEM_ERROR,
						operation: 'clearAllAlerts'
					}
				}
			);
			throw error;
		}
	}, []);

	return {
		alerts,
		showAlert,
		showSuccess,
		showError,
		showWarning,
		showInfo,
		hideAlert,
		clearAllAlerts,
	};
};
